import type { LifeDimensions } from '../domain/life-state'

const BASE_FREQUENCY_MIN = 42
const BASE_FREQUENCY_MAX = 96
const MASTER_GAIN_MAX = 0.12
const PARAMETER_RAMP_SECONDS = 0.25

export interface LifeAudioGraph {
  update(dimensions: LifeDimensions): void
  dispose(): void
}

type AudioContextFactory = () => AudioContext
type LifeAudioGraphBuilder = (context: AudioContext) => LifeAudioGraph

function clamp(value: number, minimum: number, maximum: number): number {
  const finiteValue = Number.isFinite(value) ? value : minimum
  return Math.min(maximum, Math.max(minimum, finiteValue))
}

function normalize(value: number): number {
  return clamp(value, 0, 100) / 100
}

function ramp(parameter: AudioParam, target: number, context: AudioContext): void {
  const now = context.currentTime
  const current = Number.isFinite(parameter.value) ? parameter.value : target
  parameter.cancelScheduledValues(now)
  parameter.setValueAtTime(current, now)
  parameter.linearRampToValueAtTime(target, now + PARAMETER_RAMP_SECONDS)
}

function safelyStop(node: AudioScheduledSourceNode): void {
  try {
    node.stop()
  } catch {
    // A source may already be stopped while a second lifecycle cleanup runs.
  }
}

function safelyDisconnect(node: AudioNode): void {
  try {
    node.disconnect()
  } catch {
    // A disconnected graph is already in the required final state.
  }
}

export function buildLifeAudioGraph(context: AudioContext): LifeAudioGraph {
  const master = context.createGain()
  const oscillator = context.createOscillator()
  const oscillatorGain = context.createGain()
  const noise = context.createBufferSource()
  const noiseFilter = context.createBiquadFilter()
  const noiseGain = context.createGain()
  const lfo = context.createOscillator()
  const lfoGain = context.createGain()

  master.gain.value = 0
  oscillator.type = 'sine'
  oscillator.frequency.value = BASE_FREQUENCY_MIN
  oscillatorGain.gain.value = 0.62
  noiseFilter.type = 'lowpass'
  noiseFilter.frequency.value = 320
  noiseFilter.Q.value = 0.7
  noiseGain.gain.value = 0.18
  lfo.type = 'sine'
  lfo.frequency.value = 0.08
  lfoGain.gain.value = 4

  const noiseBuffer = context.createBuffer(1, context.sampleRate * 2, context.sampleRate)
  const samples = noiseBuffer.getChannelData(0)
  for (let index = 0; index < samples.length; index += 1) {
    samples[index] = Math.random() * 2 - 1
  }
  noise.buffer = noiseBuffer
  noise.loop = true

  oscillator.connect(oscillatorGain)
  oscillatorGain.connect(master)
  noise.connect(noiseFilter)
  noiseFilter.connect(noiseGain)
  noiseGain.connect(master)
  lfo.connect(lfoGain)
  lfoGain.connect(oscillator.detune)
  master.connect(context.destination)

  oscillator.start()
  noise.start()
  lfo.start()

  const nodes: AudioNode[] = [
    oscillator,
    oscillatorGain,
    noise,
    noiseFilter,
    noiseGain,
    lfo,
    lfoGain,
    master,
  ]
  let disposed = false

  return {
    update(dimensions) {
      if (disposed) {
        return
      }

      const energy = normalize(dimensions.energy)
      const curiosity = normalize(dimensions.curiosity)
      const connection = normalize(dimensions.connection)
      const memory = normalize(dimensions.memory)
      const balance = normalize(dimensions.balance)
      const baseFrequency = BASE_FREQUENCY_MIN
        + (BASE_FREQUENCY_MAX - BASE_FREQUENCY_MIN) * (curiosity * 0.65 + energy * 0.35)
      const masterGain = clamp(
        MASTER_GAIN_MAX * (0.15 + energy * 0.5 + connection * 0.35),
        0,
        MASTER_GAIN_MAX,
      )

      ramp(oscillator.frequency, baseFrequency, context)
      ramp(oscillatorGain.gain, 0.48 + balance * 0.22, context)
      ramp(noiseFilter.frequency, 240 + curiosity * 980 + memory * 420, context)
      ramp(noiseGain.gain, 0.1 + (1 - balance) * 0.2, context)
      ramp(lfo.frequency, 0.04 + connection * 0.14, context)
      ramp(lfoGain.gain, 2 + memory * 6, context)
      ramp(master.gain, masterGain, context)
    },

    dispose() {
      if (disposed) {
        return
      }
      disposed = true
      safelyStop(oscillator)
      safelyStop(noise)
      safelyStop(lfo)
      for (const node of nodes) {
        safelyDisconnect(node)
      }
    },
  }
}

export class LifeAudio {
  private context: AudioContext | null = null
  private graph: LifeAudioGraph | null = null
  private enableOperation: Promise<void> | null = null
  private lifecycleVersion = 0
  private active = false

  constructor(
    private readonly contextFactory: AudioContextFactory =
      () => new AudioContext(),
    private readonly graphBuilder: LifeAudioGraphBuilder =
      buildLifeAudioGraph,
  ) {}

  async enable(): Promise<void> {
    if (this.active) {
      return
    }
    if (this.enableOperation) {
      return this.enableOperation
    }

    const lifecycleVersion = ++this.lifecycleVersion
    let operation!: Promise<void>
    operation = (async () => {
      let context: AudioContext | null = null
      let graph: LifeAudioGraph | null = null
      try {
        context = this.contextFactory()
        this.context = context
        graph = this.graphBuilder(context)
        this.graph = graph
        await context.resume()

        if (
          lifecycleVersion === this.lifecycleVersion
          && this.context === context
          && this.graph === graph
        ) {
          this.active = true
        }
      } catch (error) {
        if (this.graph === graph) {
          this.graph = null
          graph?.dispose()
        }
        if (this.context === context) {
          this.context = null
          await context?.close()
        }
        throw error
      } finally {
        if (this.enableOperation === operation) {
          this.enableOperation = null
        }
      }
    })()
    this.enableOperation = operation
    return operation
  }

  update(dimensions: LifeDimensions): void {
    if (!this.active) {
      return
    }
    this.graph?.update(dimensions)
  }

  async disable(): Promise<void> {
    if (!this.context && !this.graph && !this.active) {
      return
    }

    ++this.lifecycleVersion
    this.active = false
    this.enableOperation = null
    const graph = this.graph
    const context = this.context
    this.graph = null
    this.context = null

    try {
      graph?.dispose()
    } finally {
      await context?.close()
    }
  }

  get enabled(): boolean {
    return this.active
  }
}
